import { BYOKProvider } from '@developerz/domain';
import { IconShield } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AddKeyInput } from '../lib/keys';
import { baseUrlHintKey, needsBaseUrl, wantsBaseUrl } from '../lib/providerEndpoint';
import { Field } from './Field';
import { ModelChipsField } from './ModelChipsField';
import { ProviderSelect } from './ProviderSelect';
import { buttonClass } from './ui/Button';

// The donated-key form (arc 101 slice 11, plan doc 05) — <AddKeyModal/>'s field
// set plus the one thing a GIFT has that an own-account key does not: the donor's
// model ceiling. Presenter; the step above owns the two round-trips.
//
// The seal affordances are mirrored deliberately: the key is a password input,
// never logged, and lives in this local only until it is POSTed and sealed
// server-side. Nothing here ever renders a key back.

/**
 * What a donated key needs, as TWO fields rather than one merged object. It was
 * `AddKeyInput & {models}` until `AddKeyInput` grew its OWN `models` (the tier
 * tags, arc 101 slice 02): two lists, one name, two routes — and the merged
 * draft went whole to `addKey`, so the donor's ceiling was posted to the capture
 * too. Split, sending the wrong one is a compile error rather than a 400.
 */
export type DonateKeyDraft = {
  /** The capture body — `POST /v1/billing/keys`. */
  readonly capture: AddKeyInput;
  /** The donor's model ceiling — `POST /v1/donations`. */
  readonly models: readonly string[];
};

export type DonateKeyFormProps = {
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSubmit: (draft: DonateKeyDraft) => void;
};

export function DonateKeyForm(props: DonateKeyFormProps): JSX.Element {
  const [provider, setProvider] = createSignal<AddKeyInput['provider']>(BYOKProvider.Anthropic);
  const [model, setModel] = createSignal('');
  const [key, setKey] = createSignal('');
  const [baseUrl, setBaseUrl] = createSignal('');
  // `*` is the gift most donors mean — "run whatever my endpoint serves". A
  // narrower ceiling is a deliberate act, so it starts wide and is tightened.
  const [models, setModels] = createSignal<readonly string[]>(['*']);

  // The endpoint rule is `lib/providerEndpoint.ts`'s — the same seam the capture
  // form reads, rather than a second copy of it here.
  const wantsUrl = (): boolean => wantsBaseUrl(provider());
  const needsUrl = (): boolean => needsBaseUrl(provider());

  const incomplete = (): boolean =>
    model().trim() === '' ||
    key().trim() === '' ||
    models().length === 0 ||
    (needsUrl() && baseUrl().trim() === '');

  const submit = (e: Event): void => {
    e.preventDefault();
    if (incomplete()) return;
    const url = baseUrl().trim();
    props.onSubmit({
      capture: {
        provider: provider(),
        model: model().trim(),
        key: key().trim(),
        ...(wantsUrl() && url !== '' ? { base_url: url } : {}),
      },
      models: models(),
    });
  };

  const baseUrlHint = (): string | undefined => {
    const hintKey = baseUrlHintKey(provider());
    return hintKey === null ? undefined : t(hintKey);
  };

  return (
    <form class="field-group" onSubmit={submit}>
      <div class="alert alert--icon" role="note">
        <IconShield />
        <p class="alert__title">{t('account_keys.encrypted_title')}</p>
        <p class="alert__desc">{t('account_keys.encrypted_desc')}</p>
      </div>
      <Field label={t('donations.key_provider')} id="donate-key-provider">
        {/* The shared grouped picklist, `allowAny` OFF: capture's rule. */}
        {(a11y) => (
          <ProviderSelect
            {...a11y}
            label={t('donations.key_provider')}
            value={provider()}
            onChange={(value) => setProvider(value as AddKeyInput['provider'])}
            allowAny={false}
          />
        )}
      </Field>
      <Field label={t('donations.key_model')} id="donate-key-model">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            value={model()}
            placeholder={t('donations.key_model_placeholder')}
            onInput={(e) => setModel(e.currentTarget.value)}
            required
          />
        )}
      </Field>
      <Show when={wantsUrl()}>
        <Field label={t('donations.key_base_url')} id="donate-key-base-url" desc={baseUrlHint()}>
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="url"
              value={baseUrl()}
              placeholder={t('donations.key_base_url_placeholder')}
              onInput={(e) => setBaseUrl(e.currentTarget.value)}
              required={needsUrl()}
            />
          )}
        </Field>
      </Show>
      <Field label={t('donations.key_secret')} id="donate-key-secret">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="password"
            value={key()}
            placeholder={t('donations.key_secret_placeholder')}
            autocomplete="off"
            onInput={(e) => setKey(e.currentTarget.value)}
            required
          />
        )}
      </Field>
      <ModelChipsField id="donate-key-models" models={models()} onChange={setModels} />
      <Show when={props.error}>
        <p class="field__error" role="alert">
          {props.error}
        </p>
      </Show>
      <div class="dialog__footer">
        <button type="submit" class={buttonClass()} disabled={props.busy || incomplete()}>
          {t(props.busy ? 'donations.key_giving' : 'donations.key_give')}
        </button>
      </div>
    </form>
  );
}
